<!--
  - Uses the Popover API (we don't support browsers without it):
    - Dropdowns in the top-layer for escaping stacking contexts (e.g. caused by `view-transition-name`)
    - Position using CSS Anchor Positioning API if available
    - Falls back to command palette-like look when (above) not supported and/or on smaller device widths
  - Keyboard accessible
  - Focuses the filter input on open (only on non-touch devices)
  - Closes on click outside, focusout or when pressing Escape/Tab key
  - When using mouse, opens on mousedown when using the primary button, and selects options on mouseup (only when CSS Anchor Positioning API is available).

  Expected behavior described below
  ---------------------------------
  # Command palette
  ## Mouse
  - `pointerdown`@trigger -> open
  - `pointerup`@window (`target` anywhere) -> ignore
  - `click`@trigger -> ignore
  - `click`@window #1 -> ignore
  - `click`@window #2 ->
    - in/on `dropdown-inner` -> ignore
    - else ("click outside") -> close

  ## Non-mouse
  - `pointerdown`@trigger -> ignore
  - (not listening to `pointerup`@window)
  - `click`@trigger -> open
  - `click`@window ->
    - in/on `dropdown-inner` -> ignore
    - else ("click outside") -> close

  # Anchored
  ## Mouse
  - `pointerdown`@trigger
  - `pointerup`@window ->
    - in/on `option` -> selects option (and closes? does click on option close the dropdown by itself already?)
    - else: ignore
  - `click`@window #1 -> ignore
  - `click`@window #2 ->
    - in/on `dropdown-inner` -> ignore
    - else ("click outside") -> close

  ## Non-mouse
  - `pointerdown`@trigger -> ignore
  - (not listening to `pointerup`@window)
  - `click`@trigger -> open
  - `click`@window ->
    - in/on `dropdown-inner` -> ignore
    - else ("click outside") -> close
-->

<template>
  <div
    ref="rootEl"
    class="select"
    :style="{
      ['anchor-name' as string]: anchorId,
    }"
  >
    <slot
      name="trigger"
      v-bind="{
        id,
        selectedOption,
        isOpen,
        toggleDropdown,
        eventListeners: triggerEventListeners,
        dropdownId,
      }"
    >
      <div class="select__button-wrapper">
        <button
          :id="id"
          ref="triggerEl"
          type="button"
          class="select__button"
          :class="`select__button--${isOpen ? 'open' : 'closed'}`"
          :aria-expanded="isOpen"
          aria-haspopup="dialog"
          :aria-controls="dropdownId"
          v-bind="triggerEventListeners"
        >
          <slot name="selectedOption" v-bind="{ selectedOption }">
            {{ selectedOption ? selectedOption.label : placeholder }}
          </slot>
        </button>

        <IconChevronDown class="select__chevron"/>
      </div>
    </slot>

    <div
      :id="dropdownId"
      ref="dropdownEl"
      popover="manual"
      role="dialog"
      aria-label="Opties"
      class="select__dropdown"
      :class="[
        dropdownClass,
        `select__dropdown--display-type-${displayType}`,
        `select__dropdown--align-x-${alignX}`,
        `select__dropdown--prefer-open-to-${preferOpenTo}`,
      ]"
      :style="{
        ['position-anchor' as string]: anchorId,
      }"
      @focusout="delayedCloseIfFocusNotInside()"
      @toggle="dropdownOnToggle"
      @click="() => {
        /*
            NOTE:
            Suuuper weird iOS Safari bug, but if we don't keep this
            no-op event listener, there are no click events bubbling
            to the window element, meaning you can't click outside
            to close the command palette.

            So keep this! See: https://chatgpt.com/c/6990b664-e3ec-8390-9c7f-9ac206303d43
          */
      }"
    >
      <div
        ref="dropdownInnerEl"
        role="presentation"
        class="select__dropdown-inner"
      >
        <div class="select__filter-wrapper">
          <IconFilter class="select__filter-icon"/>
          <input
            :id="generateHtmlId('filter')"
            ref="dropdownFilterEl"
            v-model="filterStr"
            type="text"
            autocapitalize="none"
            spellcheck="false"
            autocomplete="off"
            class="select__filter"
            placeholder="Filter opties"
            aria-label="Filter opties"
            role="searchbox"
            :aria-controls="dropdownOptionsId"
            :aria-activedescendant="ariaActiveDescendantId"
            @input="onInput"
          >
        </div>

        <ul
          :id="dropdownOptionsId"
          ref="optionsEl"
          role="listbox"
          class="select__options"
        >
          <li
            v-for="(option, index) of filteredOptions"
            :id="getOptionHtmlId(option)"
            :key="getOptionSafeId(option)"
            :class="[
              'select__option',
              {
                'select__option--highlighted': index === highlightedOptionIndex,
                'select__option--selected': isOptionSelected(option),
                'select__option--disabled': option.disabled,
              }
            ]"
            role="option"
            :aria-selected="isOptionSelected(option)"
            :aria-disabled="option.disabled"
            tabindex="-1"
            @click="selectOption(option)"
            @pointermove="onOptionPointerMove($event, option, index)"
          >
            <slot name="option" v-bind="{ option }">
              {{ option.label }}
            </slot>
          </li>
        </ul>

        <div
          v-if="filteredOptions.length === 0"
          class="select__no-results"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          Geen resultaten gevonden.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TModel extends string | number | null, TOptionValue extends Exclude<TModel, null>[]">
  import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue';
  import { useEventListener, useMediaQuery } from '@vueuse/core';
  import { isDeepEqual } from 'remeda';
  import IconChevronDown from '@/components/svg/IconChevronDown.vue';
  import IconFilter from '@/components/svg/IconFilter.vue';
  import { useCssAnchorPositioning } from '@/composables/useCssAnchorPositioning';
  import { supportsCloseWatcher, useCloseWatcher } from '@/composables/useCloseWatcher';
  import { useDevLogger } from '@/composables/useDevLogger';
  import { canHover } from '@/helpers/mediaQueries';
  import type { SelectOption } from '@/components/Select.generics';

  type Option = SelectOption<TOptionValue[number]>;

  const {
    options,
    nullable,
    id = undefined,
    placeholder = 'Selecteer een optie',
    dropdownClass = undefined,
    alignX = 'left',
    preferOpenTo = 'bottom',
  } = defineProps<{
    options: readonly Option[];
    nullable: null extends TModel ? boolean : false;
    id?: string;
    placeholder?: string;
    dropdownClass?: string;
    alignX?: 'left' | 'right';
    preferOpenTo?: 'top' | 'bottom';
  }>();

  const modelValue = defineModel<TModel>({ required: true });

  type TriggerEventListeners = typeof triggerEventListeners;

  defineSlots<{
    trigger?(props: {
      id?: string;
      selectedOption: Option | undefined;
      isOpen: boolean;
      eventListeners: TriggerEventListeners;
      toggleDropdown: () => void;
      dropdownId: string;
    }): unknown;
    selectedOption?(props: {
      selectedOption: Option | undefined;
    }): unknown;
    option?(props: {
      option: Option;
    }): unknown;
  }>();

  const emit = defineEmits<{
    open: [triggerEl: HTMLElement | null];
    close: [triggerEl: HTMLElement | null];
  }>();

  const rootEl = useTemplateRef('rootEl');
  const triggerEl = useTemplateRef('triggerEl');
  const dropdownEl = useTemplateRef('dropdownEl');
  const dropdownInnerEl = useTemplateRef('dropdownInnerEl');
  const dropdownFilterEl = useTemplateRef('dropdownFilterEl');
  const optionsEl = useTemplateRef('optionsEl');

  const { log } = useDevLogger();

  const isBelowBpL = useMediaQuery('(width < 800px)');
  const { supportsCSSAnchorPositioning } = useCssAnchorPositioning();

  const displayType = computed(() => {
    return (isBelowBpL.value || !supportsCSSAnchorPositioning.value) ? 'command-palette' : 'anchored';
  });

  /** NOTE: This ref controls the popover state, not the other way around! */
  const isOpen = ref(false);
  const filterStr = ref('');

  /** The index of the "highlighted" option in the `filteredOptions` array */
  const highlightedOptionIndex = ref<number | null>(null);

  const internalId = useId();

  function generateHtmlId (suffix: string) {
    return `select--${internalId}__${suffix}`;
  }

  const dropdownId = generateHtmlId('dropdown');
  const dropdownOptionsId = generateHtmlId('dropdown-options');
  const anchorId = `--${generateHtmlId('anchor')}`; // a <dashed-ident> differs from a HTML id, but happen to be able to reuse this fn in this case.

  const blankOption = computed<Option>(() => ({
    label: placeholder,
    value: null as TOptionValue[number],
    disabled: false,
  }));

  const allOptions = computed(() => {
    return [
      ...(nullable ? [blankOption.value] : []),
      ...options,
    ];
  });

  const filteredOptions = computed(() => {
    if (filterStr.value.trim() === '') {
      return allOptions.value;
    }

    return allOptions.value.filter((option) =>
      option.label.toLowerCase().includes(filterStr.value.toLowerCase())
    );
  });

  const safeIdRe = /[^a-zA-Z0-9_-]/g;

  function getOptionSafeId (option: Option): string {
    let key = option.id?.toString() ?? String(option.value);
    key = key.replace(safeIdRe, '_');
    return key;
  }

  function getOptionHtmlId (option: Option): string {
    return generateHtmlId(`option--${getOptionSafeId(option)}`);
  }

  const highlightedOption = computed(() => {
    const index = highlightedOptionIndex.value;

    if (index === null) {
      return null;
    }

    return filteredOptions.value[index] ?? null;
  });

  const ariaActiveDescendantId = computed(() => (highlightedOption.value != null
    ? getOptionHtmlId(highlightedOption.value)
    : undefined));

  function isOptionSelected (option: Option) {
    return isDeepEqual(option.value, modelValue.value);
  }

  const selectedOptionIndex = computed(() => {
    return allOptions.value.findIndex((option) => isOptionSelected(option));
  });

  const selectedOption = computed(() => {
    const index = selectedOptionIndex.value;

    return index === -1
      ? undefined
      : allOptions.value[index]!;
  });

  function isOptionEnabled (option: Option) {
    return !option.disabled;
  }

  const enabledOptions = computed(() => allOptions.value.filter(isOptionEnabled));
  const enabledFilteredOptions = computed(() => filteredOptions.value.filter(isOptionEnabled));

  function close (
    autofocusTrigger = false,
    focusVisible = true,
    preventScroll = true,
  ) {
    removeAllWindowListeners();

    const dropdownElVal = dropdownEl.value;

    if (dropdownElVal && dropdownElVal.matches(':popover-open')) {
      dropdownElVal.hidePopover();
    }

    isOpen.value = false;

    const resolvedTriggerEl = getTriggerEl();

    /*
      If auto-focusing back on the trigger is desired, try to focus `triggerEl` in the
      default `trigger` slot content first. If that doesn't exist target an `<a>`,
      `<button>` or element with a `tabindex` attribute. Finally, fall back to the
      first element child of the `<Select>` root element.
    */
    if (autofocusTrigger && resolvedTriggerEl) {
      resolvedTriggerEl.focus({
        /*
          Weirdly, this does not always work. After choosing an option,
          it does focus back to the trigger, but it does not show the
          `focus-visible` styling. However, this is good enough.
        */
        focusVisible,
        // Prevents scrolling to the trigger when `close()` is called when `true`.
        preventScroll,
      });
    }
  }

  function getTriggerEl (): HTMLElement | null {
    if (!rootEl.value) {
      return null;
    }

    /*
      If auto-focusing back on the trigger is desired, try to focus `triggerEl` in the
      default `trigger` slot content first. If that doesn't exist target an `<a>`,
      `<button>` or element with a `tabindex` attribute. Finally, fall back to the
      first element child of the `<Select>` root element.
    */
    const resolvedTriggerEl = triggerEl.value
      /*
        First selects direct descendants, except the dropdown element, then queries
        it for a/button/focusable elements. We use `:scope` because without it,
        querying for direct descendants doesn't work.
        See: https://stackoverflow.com/a/17206138/3756457
      */
      ?? rootEl.value.querySelector(':scope > :not(.select__dropdown) :is(a, button, [tabindex])')
      ?? rootEl.value.firstElementChild;

    return resolvedTriggerEl instanceof HTMLElement
      ? resolvedTriggerEl
      : null;
  }

  function activeElementIsBody () {
    return document.activeElement === document.body;
  }

  function maybeFocusFilter () {
    // Ideally, we detect if the device has a virtual keyboard, but that is not possible.
    if (canHover.value) {
      dropdownFilterEl.value?.focus();
    }
  }

  function delayedCloseIfFocusNotInside () {
    // This timeout is needed, because the focusout event is triggered whenever
    // starting a click action on an option, but that option does not always
    // receive focus in time.
    setTimeout(() => {
      if (!dropdownEl.value) {
        return;
      }

      if (dropdownEl.value.contains(document.activeElement)) {
        maybeFocusFilter();
      }
      else {
        // Close, but do not auto-focus trigger and don't show focus-visible styling.
        close(false, false, false);
      }
    }, 200);
  }

  let lastPointerType: PointerEvent['pointerType'] | null = null;
  let wasJustOpenedUsingMousedown = false;
  const moveThreshold = 10; // pixels

  const triggerEventListeners = {
    onPointerdown (event: PointerEvent) {
      lastPointerType = event.pointerType;

      // We want to open on mousedown (primary mouse button), not on touch(start)
      // or when touching with pen. For those, a click is better UX.
      if (event.pointerType !== 'mouse') {
        log('[Select] pointerdown@trigger: not mouse, ignoring');
        return;
      }

      log('[Select] pointerdown@trigger: default prevented etc');

      // Should also prevent the mousedown event from firing.
      // Does not cancel the contextmenu event, which is probably fine.
      event.preventDefault();
      event.stopImmediatePropagation();

      // If not only primary button is pressed, do nothing.
      if (event.buttons !== 1) {
        log('[Select] pointerdown@trigger: not primary mouse button, ignoring');
        return;
      }

      // Early return if dropdown is already open.
      if (isOpen.value) {
        log('[Select] pointerdown@trigger: closing dropdown');
        toggleDropdown();
        return;
      }

      wasJustOpenedUsingMousedown = true;
      toggleDropdown();

      const pointerdownX = event.clientX;
      const pointerdownY = event.clientY;

      // REVIEW: Add pointercancel for when mouse button released out of window (theoretical)?
      const stop = useEventListener(window, 'pointerup', (event) => {
        stop();

        log('[Select] pointerup@trigger');

        /*
          Auto-select the option where the mouseup event happened, but only
          when using the "anchored" display type. Otherwise, the dropdown
          is not anchored to the trigger and can be far away from it, so it
          would be weird to select an option based on where the mouseup happened.

          Additionally, only auto-select if the pointer has actually moved
          from where pointerdown happened. When the dropdown flips direction
          (e.g. opens below instead of above due to lack of space), the options
          can appear right under the cursor, causing an accidental selection on
          what was meant to be a simple open click.
        */
        if (displayType.value === 'anchored') {
          const dx = event.clientX - pointerdownX;
          const dy = event.clientY - pointerdownY;
          const hasMoved = Math.sqrt(dx * dx + dy * dy) > moveThreshold;

          if (!hasMoved) {
            log('[Select] pointerup@trigger: (anchored-only) pointer did not move, not auto-clicking');
          }
          else {
            const closestOption: HTMLLIElement | null = (event.target instanceof HTMLElement)
              ? event.target.closest('.select__option')
              : null;

            if (closestOption) {
              log('[Select] pointerup@trigger: (anchored-only) option under pointer, auto-clicking option');
              closestOption.click();
            }
            else {
              log('[Select] pointerup@trigger: (anchored-only) no option under pointer, so not auto-clicking any');
            }
          }
        }
      }, { capture: true });
    },
    onClick (event: PointerEvent | MouseEvent) {
      log('[Select] click@trigger: default prevented etc');
      event.preventDefault();
      event.stopImmediatePropagation();

      if (
        (
          event instanceof PointerEvent
          && event.pointerType !== 'mouse'
        )
        /*
          This fixes an issue in iOS Safari, where a "click" event seems to be synthesized
          by Safari artificially and it sets the `event.pointerType` to `"mouse"`.
          This may be a Browserstack-only issue, but just to be sure, this makes
          sure the touch-based "click" is not ignored.
        */
        || lastPointerType !== 'mouse'
      ) {
        toggleDropdown();
      }
      else {
        log('[Select] click@trigger: mouse, ignoring (we use pointerdown for mouse)');
      }
    },
    onKeydown (event: KeyboardEvent) {
      switch (event.key) {
        // Allow opening the dropdown with the Space/Enter key.
        // Also, why the hell is space not 'Space'?!
        case ' ':
        case 'Enter':
          event.preventDefault();
          event.stopPropagation();
          toggleDropdown();
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (event.altKey) {
            toggleDropdown();
          }
          else {
            selectPreviousEnabledOption();
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (event.altKey) {
            toggleDropdown();
          }
          else {
            selectNextEnabledOption();
          }
          break;
        case 'Home':
          event.preventDefault();
          selectFirstEnabledOption();
          break;
        case 'End':
          event.preventDefault();
          selectLastEnabledOption();
          break;
        case 'Delete':
        case 'Backspace':
          if (nullable && modelValue.value !== null) {
            event.preventDefault();
            event.stopPropagation();
            modelValue.value = null as TModel;
          }
          break;
        // IDEA: Browsers actually cycle through options. There's some sort of timeout and if you type within that timeout, you can select an option based on what you type.
        // default:
        //   // Select first option matching pressed letter
        //   if (event.key.length === 1) {
        //     const pressedKey = event.key.toLowerCase();

        //     const firstOptionStartingWithPressedKey = enabledOptions.value.find((option) =>
        //       option.label.toLowerCase().startsWith(pressedKey)
        //     );

        //     if (firstOptionStartingWithPressedKey) {
        //       selectOption(firstOptionStartingWithPressedKey);
        //     }
        //   }
      }
    },
  } as const;

  function toggleDropdown () {
    log('[Select] toggling dropdown to:', !isOpen.value);
    isOpen.value = !isOpen.value;
  }

  function selectOption (option: Option) {
    // Cannot select a disabled option, so just return.
    if (option.disabled) {
      return;
    }

    modelValue.value = option.value;
    close(true, true, false);
  }

  function scrollOptionIntoView (
    optionIndex: number,
    scrollBehavior: ScrollBehavior = 'smooth',
  ) {
    optionsEl.value?.children[optionIndex]?.scrollIntoView({
      behavior: scrollBehavior,
      block: 'nearest',
      inline: 'nearest',
    });
  }

  function selectPreviousEnabledOption () {
    switch (enabledOptions.value.length) {
      case 0: return;
      case 1:
        selectOption(enabledOptions.value[0]!);
        return;
    }

    const indexInEnabledOptions = selectedOption.value
      ? enabledOptions.value.indexOf(selectedOption.value)
      : -1;

    // If `-1`, that means there is no selected option or the selected option is not enabled.
    // In those cases, we just select the last enabled option.
    if (indexInEnabledOptions === -1) {
      selectOption(enabledOptions.value.at(-1)!);
      return;
    }

    const newSelectedOptionIndex = Math.max(indexInEnabledOptions - 1, 0);
    selectOption(enabledOptions.value[newSelectedOptionIndex]!);
  }

  function selectNextEnabledOption () {
    switch (enabledOptions.value.length) {
      case 0: return;
      case 1:
        selectOption(enabledOptions.value[0]!);
        return;
    }

    const indexInEnabledOptions = selectedOption.value
      ? enabledOptions.value.indexOf(selectedOption.value)
      : -1;

    // If `-1`, that means there is no selected option or the selected option is not enabled.
    // In those cases, we just select the first enabled option.
    if (indexInEnabledOptions === -1) {
      selectOption(enabledOptions.value[0]!);
      return;
    }

    const newSelectedOptionIndex = Math.min(indexInEnabledOptions + 1, enabledOptions.value.length - 1);
    selectOption(enabledOptions.value[newSelectedOptionIndex]!);
  }

  function selectFirstEnabledOption () {
    const firstEnabledOption = enabledOptions.value[0];

    if (firstEnabledOption) {
      selectOption(firstEnabledOption);
    }
  }

  function selectLastEnabledOption () {
    const lastEnabledOption = enabledOptions.value.at(-1);

    if (lastEnabledOption) {
      selectOption(lastEnabledOption);
    }
  }

  /**
   * Set the first enabled filtered option as highlighted.
   *
   * If there is no enabled filtered option to be found,
   * it sets `highlightedOptionIndex` to `null`. This can happen
   * when all options are disabled or when the filter
   * only finds disabled options.
   */
  function highlightFirstEnabledFilteredOption () {
    const firstEnabledFilteredOptionIndex = filteredOptions.value.findIndex(isOptionEnabled);

    highlightedOptionIndex.value = firstEnabledFilteredOptionIndex > -1
      ? firstEnabledFilteredOptionIndex
      : null;
  }

  /**
   * Set the last enabled filtered option as highlighted.
   *
   * If there is no enabled filtered option to be found,
   * it sets `highlightedOptionIndex` to `null`. This can happen
   * when all options are disabled or when the filter
   * only finds disabled options.
   */
  function highlightLastEnabledFilteredOption () {
    const lastEnabledFilteredOptionIndex = filteredOptions.value.findLastIndex(isOptionEnabled);

    highlightedOptionIndex.value = lastEnabledFilteredOptionIndex > -1
      ? lastEnabledFilteredOptionIndex
      : null;
  }

  function highlightPreviousEnabledFilteredOption () {
    const currentlyEnabledFilteredOptions = enabledFilteredOptions.value;
    const currentlyEnabledFilteredOptionsCount = currentlyEnabledFilteredOptions.length;

    if (currentlyEnabledFilteredOptions.length === 0) {
      highlightedOptionIndex.value = null;
      return;
    }

    const currentlyHighlightedOption = highlightedOption.value;

    /**
     * The index of the currently highlighted option in `enabledFilteredOptions`.
     * Will be `-1` if there is no highlighted option or if the highlighted
     * option is not enabled or displayed with the current filter.
     */
    const highlightedOptionIndexInEnabledFilteredOptions = currentlyHighlightedOption
      ? currentlyEnabledFilteredOptions.indexOf(currentlyHighlightedOption)
      : -1;

    if (
      highlightedOptionIndexInEnabledFilteredOptions === -1
      || currentlyEnabledFilteredOptionsCount === 1
    ) {
      highlightLastEnabledFilteredOption();
    }
    else {
      const previousEnabledOption = currentlyEnabledFilteredOptions[
        (highlightedOptionIndexInEnabledFilteredOptions - 1 + currentlyEnabledFilteredOptionsCount) % currentlyEnabledFilteredOptionsCount
      ]!;

      highlightedOptionIndex.value = filteredOptions.value.indexOf(previousEnabledOption);
    }

    scrollOptionIntoView(highlightedOptionIndex.value!);
  }

  function highlightNextEnabledFilteredOption () {
    const currentlyEnabledFilteredOptions = enabledFilteredOptions.value;
    const currentlyEnabledFilteredOptionsCount = currentlyEnabledFilteredOptions.length;

    if (currentlyEnabledFilteredOptions.length === 0) {
      highlightedOptionIndex.value = null;
      return;
    }

    const currentlyHighlightedOption = highlightedOption.value;

    /**
     * The index of the currently highlighted option in `enabledFilteredOptions`.
     * Will be `-1` if there is no highlighted option or if the highlighted
     * option is not enabled or displayed with the current filter.
     */
    const highlightedOptionIndexInEnabledFilteredOptions = currentlyHighlightedOption
      ? currentlyEnabledFilteredOptions.indexOf(currentlyHighlightedOption)
      : -1;

    if (
      highlightedOptionIndexInEnabledFilteredOptions === -1
      || currentlyEnabledFilteredOptionsCount === 1
    ) {
      highlightFirstEnabledFilteredOption();
    }
    else {
      const nextEnabledOption = currentlyEnabledFilteredOptions[
        (highlightedOptionIndexInEnabledFilteredOptions + 1) % currentlyEnabledFilteredOptionsCount
      ]!;

      highlightedOptionIndex.value = filteredOptions.value.indexOf(nextEnabledOption);
    }

    scrollOptionIntoView(highlightedOptionIndex.value!);
  }

  function onInput () {
    highlightFirstEnabledFilteredOption();
  }

  function onOptionPointerMove (event: PointerEvent, option: Option, optionIndex: number) {
    if (!isOpen.value || event.pointerType !== 'mouse') {
      return;
    }

    if (option.disabled) {
      return;
    }

    if (highlightedOptionIndex.value !== optionIndex) {
      highlightedOptionIndex.value = optionIndex;
    }
  }

  function dropdownOnToggle (event: ToggleEvent) {
    const newStateIsOpen = event.newState === 'open';
    const resolvedTriggerEl = getTriggerEl();

    // This check prevents triggering the watch on `isOpen` again for no reason.
    if (isOpen.value !== newStateIsOpen) {
      // Since `isOpen` controls the popover state, not the other way around,
      // this shouldn't be necessary. However, if it does, we want to be in sync.
      isOpen.value = newStateIsOpen;
    }

    if (newStateIsOpen) {
      emit('open', resolvedTriggerEl);
    }
    else {
      emit('close', resolvedTriggerEl);
    }

    const el = dropdownEl.value!;

    if (!newStateIsOpen) {
      /*
        EXCEPTION:
        Sorry for this timeout, but apparently there is no `transitionend` event
        transitioning with discrete animations (we transition to `display: none`)?!
        Long live "modern" APIs...

        Anyway, this has one benefit: if you quickly close and reopen,
        your `filter` and `highlightedOptionIndex` are preserved.
      */
      setTimeout(() => {
        if (window.getComputedStyle(el).display === 'none') {
          filterStr.value = '';
          highlightedOptionIndex.value = null;
        }
      }, 400);
    }
  }

  function useWatchClose () {
    return useCloseWatcher({
      onClose (event) {
        log('[Select] close event from CloseWatcher:', event);
        close(true, true, false);
      },
    });
  }

  function useWindowOnKeydown () {
    return useEventListener(window, 'keydown', (event) => {
      // Safe-guard: if dropdown isn't open, get rid of all listeners.
      if (!isOpen.value) {
        removeAllWindowListeners();
        return;
      }

      const preventAndStop = () => {
        event.preventDefault();
        // Prevent other window event listeners from firing.
        event.stopImmediatePropagation();
      };

      switch (event.key) {
        case 'ArrowUp':
          preventAndStop();
          highlightPreviousEnabledFilteredOption();
          break;
        case 'ArrowDown':
          preventAndStop();
          highlightNextEnabledFilteredOption();
          break;
        case 'Home':
          // Only when there is no filter, since we need to consider the text input cursor as holy.
          if (filterStr.value.trim()) {
            break;
          }

          preventAndStop();
          highlightFirstEnabledFilteredOption();
          break;
        case 'End':
          // Only when there is no filter, since we need to consider the text input cursor as holy.
          if (filterStr.value.trim()) {
            break;
          }

          preventAndStop();
          highlightLastEnabledFilteredOption();
          break;
        case 'Enter':
          // Only relevant in some languages, not in Dutch.
          if (event.isComposing) {
            break;
          }
          preventAndStop();
          if (highlightedOption.value) {
            selectOption(highlightedOption.value!);
          }
          break;
        case 'Escape':
          if (!supportsCloseWatcher()) {
            preventAndStop();
            close(true, true, false);
          }
          break;
        /*
          NOTE:
          There are two defendable approaches to handling Tab key presses here:
          - The "native" way:
            - Either Tab or Shift+Tab simply closes the dropdown list and moves focus to the trigger element.
            -> event.preventDefault() + focus trigger
          - The "modern" way (ARIA Authoring Practices Guide):
            - Tab immediately closes the dropdown list and selects the next focusable element in the DOM.
            - Shift+Tab immediately closes the dropdown list and selects the previous focusable element in the DOM, which is the trigger button.
            -> Don't event.preventDefault(), just close the dropdown and let the browser handle the focus movement as it normally would.

          I chose the "native" way, because it feels more natural to me.
        */
        case 'Tab':
          // Only the dropdown filter input is focusable,
          // so we can just close the dropdown when pressing tab.
          preventAndStop();
          close(true, true, false);
          break;
      }
    }, { capture: true, passive: false });
  }

  function useWindowOnClick () {
    return useEventListener(window, 'click', (event) => {
      log('[Select] click@window');

      // Safe-guard: if dropdown isn't open, get rid of all listeners.
      if (!isOpen.value) {
        removeAllWindowListeners();
        return;
      }

      /*
        Ignore first click event after dropdown was opened using mousedown.

        At this point:
        - We opened the dropdown already on mousedown (`pointerdown` + mouse), setting `isOpen` -> `true`.
        - The watcher on `isOpen` is triggered and we added this window click listener.
        - The `pointerup` event listener callback has been executed.
        - The `click` event is now firing, but we want to ignore it, because it is
          the "click" part of the same mouse interaction that opened the dropdown,
          and we want to prevent immediately closing the dropdown again.
      */

      if (wasJustOpenedUsingMousedown) {
        log('[Select] click@window: ignoring click just after opening dropdown with mousedown and forget mousedown');
        wasJustOpenedUsingMousedown = false;
        return;
      }

      // Fixes HMR issue while working on this component.
      if (!dropdownInnerEl.value) {
        removeAllWindowListeners();
        return;
      }

      // REVIEW: When exactly does this happen and what do we want to happen in those cases?
      if (!(event.target instanceof Element)) {
        return;
      }

      if (dropdownInnerEl.value.contains(event.target)) {
        log('[Select] click@window: clicked in/on dropdown-inner element, so ignoring');
      }
      else {
        log('[Select] click@window: clicked outside, closing');

        if (displayType.value === 'command-palette') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }

        // Only auto-focus trigger if the click outside caused focus
        // to go back to the <body>. Any other element having
        // focus now probably has it intentionally.
        close(activeElementIsBody(), false, true);
      }
    }, { capture: true, passive: false });
  }

  const cleanups: (() => void)[] = [];

  function addAllWindowListeners () {
    if (cleanups.length) {
      removeAllWindowListeners();
    }

    log('[Select] adding all window listeners');
    cleanups.push(useWatchClose());
    cleanups.push(useWindowOnKeydown());
    cleanups.push(useWindowOnClick());
  }

  function removeAllWindowListeners () {
    if (cleanups.length) {
      log('[Select] removing all window listeners');
      cleanups.forEach((cleanup) => cleanup());
      cleanups.length = 0;
    }
  }

  watch(isOpen, (newVal, oldVal) => {
    // NOTE: Prevent recursion with the 'toggle' event, which modifies `isOpen`.
    if (newVal === oldVal) {
      return;
    }

    removeAllWindowListeners();

    const dropdownElVal = dropdownEl.value;

    if (dropdownElVal) {
      const dropdownElHasPopoverOpen = dropdownElVal.matches(':popover-open');
      const resolvedTriggerEl = getTriggerEl();

      // NOTE: We show/hide the popover a tick before the DOM is updated by ref changes further down below.
      if (newVal && !dropdownElHasPopoverOpen) {
        dropdownElVal.showPopover(resolvedTriggerEl ? {
          source: resolvedTriggerEl,
        } : undefined);

        addAllWindowListeners();
      }
      else if (!newVal && dropdownElHasPopoverOpen) {
        dropdownElVal.hidePopover();
      }
    }

    if (newVal) {
      highlightedOptionIndex.value = filteredOptions.value.findIndex((option) => option.value === modelValue.value);

      if (highlightedOptionIndex.value === -1) {
        highlightFirstEnabledFilteredOption();
      }

      nextTick(() => {
        // Maybe focus on the filter input after opening dropdown.
        maybeFocusFilter();

        // Autoscroll to selected option after opening dropdown.
        if (highlightedOptionIndex.value !== null) {
          scrollOptionIntoView(highlightedOptionIndex.value, 'instant');
        }
      });
    }
  }, {
    flush: 'sync',
  });

  // When the selected option becomes disabled, reset the model value.
  watch(selectedOption, (newVal) => {
    if (newVal?.disabled) {
      if (nullable) {
        modelValue.value = null as TModel;
      }
      else {
        highlightFirstEnabledFilteredOption();
      }
    }
  }, { deep: true, immediate: true });

  // When the highlighted option becomes disabled, reset `highlightedIndex`
  // to the first enabled filtered option or `null`.
  watch(highlightedOption, (newVal) => {
    if (newVal?.disabled) {
      highlightFirstEnabledFilteredOption();
    }
  }, { deep: true, immediate: true });

  defineExpose({
    close,
  });
</script>

<style lang="scss">
  .select {
    // svg target size = 26px
    // root font size = based on 16px
    $option-svg-size-factor: math.div(26, 16);
    $option-padding-top: em(11);
    $option-padding-bottom: em(10);
    $option-padding-left: em(12);
    $option-padding-right: em(18);

    position: relative;

    /// When using `<Select>` with a custom trigger slot, you can wrap
    /// its contents with `<div class="select__button-wrapper">` to get select styling.
    &__button-wrapper {
      width: 100%;
      display: grid;
      grid-template-areas: "select";
      align-items: center;
    }

    // NOTE: .select__button is mainly styled in global base styles to match input styles.
    &__button {
      padding-right: 2em;
      padding-left: 12px;
      align-content: center;
      text-align: left;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      cursor: pointer;
      box-shadow: inset 0 0 0 1px var(--color-gray-300);
      background-image: linear-gradient(to bottom, #fff 0%, #f6f6f6 100%);
      background-repeat: no-repeat;
      background-position: 0 0;
      background-size: 100%;

      &:hover {
        box-shadow: inset 0 0 0 2px var(--color-gray-500);
      }

      &:focus {
        outline: none !important;
        box-shadow: inset 0 0 0 2px var(--color-primary);
      }

      // If the button contains markup, we use equal padding top and bottom,
      // because it's probably an image or SVG.
      &:has(> *) {
        padding-top: 0;
        padding-bottom: 0;
      }

      > * {
        display: block;
      }

      &.select__button--open {
        border-color: var(--input-border-color-focus, var(--color-primary));
      }
    }

    &__button,
    &__chevron {
      grid-area: select;
    }

    &__chevron {
      justify-self: end;
      margin-right: 0.7em;
      width: input-em(11);
      color: var(--color-primary);
      pointer-events: none;
      translate: 0 10%;
    }

    &__dropdown {
      // Some CSS resets are needed because of silly [popover] styles: https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Selectors/:popover-open#examples
      // Style properties that are also in the popover default styles are indicated with a "(popover CSS)" comment.
      flex-direction: column;

      // min-width/width/height are set way down below
      color: inherit; // (popover CSS)
      background-color: transparent; // (popover CSS)
      border: none; // (popover CSS)
      overflow: visible; // (popover CSS)
      user-select: none;
      position: fixed; // (popover CSS)
      padding: 0; // (popover CSS)
      margin: unset; // (popover CSS)

      // https://developer.mozilla.org/en-US/docs/Web/API/Popover_API/Using#transitioning_a_popover
      // NOTE: Exit transition doesn't work in Firefox due to this bug: https://bugzilla.mozilla.org/show_bug.cgi?id=1971162
      transition-property: display, overlay;
      transition-duration: var(--speed-xxs);
      transition-timing-function: var(--easing-ease);
      transition-behavior: allow-discrete;

      .select__dropdown-inner {
        transition-property: scale, opacity;
        transition-duration: inherit;
        transition-timing-function: inherit;
        background-color: var(--color-white);
        border-radius: 4px;
        border: 1px solid color-opacity(--color-black, 8%);
        box-shadow:
          inset 0 0 0 1px color-opacity(--color-black, 4%),
          0 10px 24px color-opacity(--color-black, 13%);
        overflow: hidden;
      }

      &:not(:popover-open) {
        display: none;

        .select__dropdown-inner {
          opacity: 0;
          scale: 0.9;
        }
      }

      @starting-style {
        &:popover-open .select__dropdown-inner {
          opacity: 0;
          scale: 0.9;
        }
      }
    }

    &__filter-wrapper {
      position: relative;
      width: calc(100% - 16px);
      margin: 8px;
    }

    &__filter-icon {
      position: absolute;
      left: $option-padding-left;
      top: 50%;
      translate: 0 -45%;
      width: 1em;
      height: 1em;
      color: var(--color-black);
      pointer-events: none;
      z-index: 1;
    }

    &__filter {
      width: 100%;
      margin: 0;
      border-radius: 4px;
      box-shadow: inset 0 0 0 1px var(--color-gray-300);
      background-color: var(--color-white);
      padding-left: calc(#{$option-padding-left} + 1em + 8px);
      padding-right: $option-padding-right;

      &:not(:disabled):hover {
        box-shadow: inset 0 0 0 1px var(--color-gray-500);
        background-color: var(--color-white);
      }

      &:not(:disabled):focus {
        box-shadow: inset 0 0 0 2px var(--color-primary);
        background-color: var(--color-white);
      }
    }

    &__options {
      max-height: 400px;
      overflow-y: auto;
      list-style: none;
      padding: 0;
      margin: 0;

      // Prevent scroll chaining to the body when reaching the end of the options list on touch devices.
      overscroll-behavior: contain;
    }

    &__option,
    &__no-results {
      display: flex;
      align-items: center;
      padding: calc(#{$option-padding-top} + var(--ui-font-correction-y)) $option-padding-right $option-padding-bottom $option-padding-left;
      min-height: calc(#{$option-svg-size-factor * 1em} + #{$option-padding-top} + var(--ui-font-correction-y) + #{$option-padding-bottom});
    }

    &__option {
      cursor: pointer;
      transition: background-color var(--speed-fast) var(--easing-cubic-2);
      outline: none;

      // If the option contains markup, we use equal padding top and bottom,
      // because it's probably an image or SVG.
      &:has(> *) {
        padding-top: math.div($option-padding-top + $option-padding-bottom, 2);
        padding-bottom: math.div($option-padding-top + $option-padding-bottom, 2);
      }

      &--disabled {
        color: var(--color-gray-500) !important;
        cursor: default !important;
      }

      &:not(&--disabled) {
        // If no option is currently in :active state, apply highlighted styles to the highlighted option.
        .select__options:not(:has(&:active)) &.select__option--highlighted {
          background-color: color-mix(in oklch, var(--color-lichtroze), white 65%);
          transition-duration: 0s;
        }

        &:active {
          background-color: color-mix(in oklch, var(--color-lichtroze), white 40%) !important;
          transition-duration: 0s !important;
        }

        &.select__option--selected {
          font-weight: 700;
        }
      }

      &:last-child {
        margin-bottom: 8px;
      }
    }
  }

  .select__dropdown--display-type-anchored {
    --speed-xxs: var(--speed-instant);

    // IDEA: We can span the entire anchor if we want, but maybe `position-area: <top/bottom> span-all` is better for that.
    min-width: clamp(200px, anchor-size(width), 600px);
    max-width: 600px;
    width: fit-content; // (popover CSS)
    height: fit-content; // (popover CSS)
    position-visibility: always; // don't hide dropdown when anchor out of screen
    position-try-order: most-height;
    position-try-fallbacks: flip-block;

    --dropdown-distance: 8px;

    /*
      You can't do `transform-origin` in `@position-try`, but we can in Anchored Container Queries.
      See: https://developer.chrome.com/blog/anchored-container-queries
      Note that we have to set a default `transform-origin` for when no fallbacks are used!
      Yes, you read that right, it's not that there's always one active fallback: https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/position-try-order#try-size
    */
    @include supports-anchored-container {
      container-type: anchored;
    }

    &.select__dropdown--prefer-open-to-top:is(.select__dropdown--align-x-left) {
      position-area: bottom span-right;
      margin: var(--dropdown-distance) 0 0;

      @include supports-anchored-container {
        .select__dropdown-inner {
          transform-origin: top left;

          @container anchored(fallback: flip-block) {
            transform-origin: bottom left;
          }
        }
      }
    }

    &.select__dropdown--prefer-open-to-bottom:is(.select__dropdown--align-x-left) {
      position-area: top span-right;
      margin: 0 0 var(--dropdown-distance);

      @include supports-anchored-container {
        .select__dropdown-inner {
          transform-origin: bottom left;

          @container anchored(fallback: flip-block) {
            transform-origin: top left;
          }
        }
      }
    }

    &.select__dropdown--prefer-open-to-top:is(.select__dropdown--align-x-right) {
      position-area: bottom span-left;
      margin: var(--dropdown-distance) 0 0;

      @include supports-anchored-container {
        .select__dropdown-inner {
          transform-origin: top right;

          @container anchored(fallback: flip-block) {
            transform-origin: bottom right;
          }
        }
      }
    }

    &.select__dropdown--prefer-open-to-bottom:is(.select__dropdown--align-x-right) {
      position-area: top span-left;
      margin: 0 0 var(--dropdown-distance);

      @include supports-anchored-container {
        .select__dropdown-inner {
          transform-origin: bottom right;

          @container anchored(fallback: flip-block) {
            transform-origin: top right;
          }
        }
      }
    }
  }

  .select__dropdown--display-type-command-palette {
    --speed-xxs: var(--speed-fast);

    position-anchor: none !important;
    inset: 0;
    width: stretch;

    // --vvh makes sure this shrinks height when the mobile keyboard is visible
    height: var(--vvh, stretch);
    place-items: safe center;
    transition-duration: var(--speed-xxs);

    &::backdrop {
      opacity: 0;
      pointer-events: none !important;
      background-color: rgb(0 0 0 / 35%);
      transition-property: display, overlay, opacity;
      transition-duration: var(--speed-xxs);
      transition-timing-function: var(--easing-ease);
      transition-behavior: allow-discrete;
    }

    &:popover-open {
      display: grid;

      &::backdrop {
        opacity: 1;

        @starting-style {
          opacity: 0;
        }
      }
    }

    .select__dropdown-inner {
      --select-dropdown-inner-margin: 30px;

      // Carefully set up, please don't just blindly change without testing a lot!
      min-width: 200px;
      width: stretch;
      max-width: 600px;

      // ensures the margin box never exceeds --vvh
      max-height: calc(var(--vvh, 100dvh) - 2 * var(--select-dropdown-inner-margin));
      margin: var(--select-dropdown-inner-margin);
      display: flex;
      flex-direction: column;
    }
  }
</style>
